'use client'

import { Calendar } from 'lucide-react'
import AuditForm from '@/components/AuditForm'
import Footer from '@/components/Footer'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'
import '@/app/newlp/styles.css'

const CALENDAR_URL = 'https://calendar.app.google/b1Mz4DmS6jEEfmYW8'

const AVATAR = '/images/tomas.jpg'
const WALLPAPER = '/images/homepage/cta.avif'

const REVIEWS_URL =
  'https://www.google.com/maps/place/SmartMetrics/@46.423669,-129.9427085,3z/data=!4m8!3m7!1s0x80d954abfbff295d:0x9f066acf6ab522e4!8m2!3d46.423669!4d-129.9427086!9m1!1b1!16s%2Fg%2F11fy4q0lsm?entry=ttu'

const STATS = [
  { value: '20 yrs', label: 'marketing & analytics' },
  { value: 'Direct', label: 'no account managers' },
] as const

type QuoteCtaProps = {
  showFooter?: boolean
  embedded?: boolean
  heading?: string
  buttonText?: string
  formSource?: string
  serviceName?: string
  focusAreaPlaceholder?: string
  hideStats?: boolean
  variant?: 'quote' | 'classic'
}

const QUOTES = [
  {
    name: 'Gray Digital Group',
    logo: '/images/clients/graydigital.png',
    quote: 'Tomas at SmartMetrics has been fantastic to work with. He consistently delivers high-quality work quickly, communicates clearly and proactively, and makes even complex implementations feel straightforward.',
  },
  {
    name: 'Blitzy',
    logo: '/images/clients/blitzy.png',
    quote: 'A huge difference to the business, and we know where the next dollar goes.',
  },
  {
    name: 'PermitFlow',
    logo: '/images/clients/permitflow.png',
    quote: 'We can scale spend now without guessing what is actually working.',
  },
  {
    name: 'Dj Haer Jr',
    logo: '/images/testimonials/dj.png',
    quote: 'Extremely knowledgable & capable!',
  },
] as const

const FORM_FIELD_CLASS =
  '[&_form>div]:!space-y-2.5 [&_form>div_.grid]:!gap-2.5 [&_textarea]:min-h-[88px] [&_textarea]:text-sm [&_input]:h-11 [&_input]:text-sm [&_input]:rounded-lg [&_textarea]:rounded-lg [&_input]:border-[rgba(18,32,58,0.1)] [&_textarea]:border-[rgba(18,32,58,0.1)] [&_#website]:rounded-none [&_#website]:border-0 [&_form_.rounded-lg]:rounded-lg [&_form_.rounded-lg]:border-[rgba(18,32,58,0.1)] [&_button[type=submit]]:!h-11 [&_button[type=submit]]:!text-sm [&_button[type=submit]]:!rounded-lg [&_button[type=submit]]:!bg-[#13479c] [&_button[type=submit]]:hover:!bg-[#0f3a7a] [&_button[type=submit]]:!shadow-[0_8px_18px_-6px_rgba(19,71,156,0.45)] [&_.absolute.-inset-px]:!hidden'

function QuoteVariantB({
  showFooter = false,
  heading = 'Get a Quote',
  buttonText = 'Get a Quote',
  formSource = 'services-whitelabel-cta',
  serviceName,
  focusAreaPlaceholder = 'Tell me more about your project',
}: QuoteCtaProps) {
  return (
    <section className="relative w-full px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-[28px] bg-white shadow-[0_24px_50px_-24px_rgba(18,32,58,0.3)]">
        <section id="testimonials" className="scroll-mt-24 bg-[#f4f7fc] px-6 py-8 sm:px-8 sm:py-10 lg:px-10" aria-label="What clients say">
          <div className="grid items-center gap-8 border-b border-[rgba(18,32,58,0.1)] pb-8 lg:grid-cols-[minmax(0,1fr)_440px]">
            <div className="flex flex-col gap-3">
              <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-[#13479c]">What clients say</p>
              <div className="text-[16px] tracking-[2px] text-[#FDB241]" aria-label="5 stars">★★★★★</div>
              <p className="text-[18px] font-medium leading-snug tracking-[-0.02em] text-[#12203A] sm:text-[20px]">
                “{QUOTES[0].quote}”
              </p>
              <div className="flex items-center gap-2.5 text-[15px] font-semibold">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={QUOTES[0].logo} alt="" className="h-9 w-9 rounded-full border border-[rgba(18,32,58,0.1)] bg-white object-contain p-[3px]" />
                {QUOTES[0].name}
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/images/reviews.webp" alt="Client reviews" className="w-full lg:max-w-[440px]" />
              <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#12203A]">
                <span className="relative h-4 w-4 shrink-0 rounded-full bg-[conic-gradient(#ea4335_0_25%,#fbbc05_25%_45%,#34a853_45%_70%,#4285f4_70%)]" aria-hidden>
                  <span className="absolute inset-[3.5px] rounded-full bg-white" />
                </span>
                5.0 on Google ·{' '}
                <a href={REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="text-[#13479c] underline underline-offset-[3px] hover:text-[#0f3a7a]">
                  All reviews
                </a>
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 pt-6 sm:grid-cols-3 sm:gap-0">
            {QUOTES.slice(1).map((item, index) => (
              <div
                key={item.name}
                className={`flex flex-col gap-2.5 ${index > 0 ? 'sm:border-l sm:border-[rgba(18,32,58,0.1)] sm:pl-6' : ''}`}
              >
                <div className="text-[13px] tracking-[1px] text-[#FDB241]" aria-label="5 stars">★★★★★</div>
                <p className="text-[15px] leading-snug text-[#12203A]">“{item.quote}”</p>
                <div className="mt-auto flex items-center gap-2 text-[13px] font-semibold">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={item.logo} alt="" className="h-7 w-7 shrink-0 rounded-full border border-[rgba(18,32,58,0.1)] bg-white object-contain p-[2px]" />
                  {item.name}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white px-6 py-8 sm:px-8 sm:py-10 lg:px-10" aria-label={heading}>
          <div className="grid items-start gap-8 lg:grid-cols-[400px_minmax(0,1fr)]">
            <div className="flex flex-col gap-5">
              <div className="overflow-hidden rounded-2xl bg-[#f4f7fc]" style={{ aspectRatio: '1368 / 960' }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/images/tomas_call.webp" alt="Video call with Tomas" className="h-full w-full object-cover" />
              </div>
              <a
                href={CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="-mt-2 inline-flex items-center justify-center gap-1.5 self-center text-[13px] font-medium text-[#13479c] hover:text-[#0f3a7a]"
                onClick={() => trackPostHogEvent('cta_click', {
                  cta_text: 'Schedule a 15min call',
                  cta_location: formSource,
                  cta_type: 'calendar',
                })}
              >
                <Calendar className="h-3.5 w-3.5" aria-hidden />
                Schedule a 15min call
              </a>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t border-[rgba(18,32,58,0.1)] pt-4">
                <div className="flex gap-5">
                  <div>
                    <b className="block text-xl font-bold leading-none tracking-[-0.03em]">20 yrs</b>
                    <span className="text-[13px] text-[rgba(18,32,58,0.52)]">experience</span>
                  </div>
                  <div>
                    <b className="block text-xl font-bold leading-none tracking-[-0.03em]">
                      5.0 <span className="text-[#FDB241]">★</span>
                    </b>
                    <a href={REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#13479c] underline underline-offset-[3px] hover:text-[#0f3a7a]">
                      Reviews
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src="/images/badges/salesforce.png" alt="Salesforce Certified" className="h-10 w-auto object-contain" />
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src="/images/badges/zoho.png" alt="Zoho Certified" className="h-10 w-auto object-contain" />
                </div>
              </div>
            </div>

            <div id="get-quote" className="flex scroll-mt-24 flex-col gap-4 rounded-3xl bg-[#f4f7fc] px-5 py-6 sm:px-6">
              <div>
                <h2 className="text-[30px] font-bold leading-[1.08] tracking-[-0.03em] text-[#12203A] sm:text-[36px]">
                  Work with Tomas
                </h2>
                <p className="mt-1 text-[12px] font-medium text-[rgba(18,32,58,0.52)]">Founder, San Diego, CA</p>
                <p className="mt-2 text-[15px] leading-snug text-[rgba(18,32,58,0.64)]">
                  20 years in marketing, CRO, CRM, and analytics. Now building the systems AI runs on.
                </p>
              </div>
              <div className={FORM_FIELD_CLASS}>
                <AuditForm
                  layout="column"
                  serviceType="monthly"
                  buttonText={buttonText}
                  ctaText="Tell us what you need"
                  formSource={formSource}
                  serviceName={serviceName}
                  focusAreaPlaceholder={focusAreaPlaceholder}
                />
              </div>
              <p className="text-center text-[13px] leading-snug text-[rgba(18,32,58,0.64)]">
                You work with me directly, from scope to delivery. No account managers.
              </p>
            </div>
          </div>
        </section>
      </div>
      {showFooter && <Footer />}
    </section>
  )
}

export default function WhitelabelCTA(props: QuoteCtaProps) {
  if (props.variant === 'classic') return <ClassicQuote {...props} />
  return <QuoteVariantB {...props} />
}

function ClassicQuote({
  showFooter = false,
  embedded = false,
  heading = 'Get a project quote',
  buttonText = 'Get a project quote',
  formSource = 'services-whitelabel-cta',
  serviceName,
  focusAreaPlaceholder = 'Tell me more about your project',
  hideStats = false,
}: QuoteCtaProps) {
  return (
    <section
      id="get-quote"
      className={
        embedded
          ? 'relative w-full'
          : `relative isolate w-full pt-16 sm:pt-20 lg:pt-24 ${showFooter ? 'pb-0' : 'pb-16 sm:pb-20 lg:pb-24'}`
      }
    >
      {!embedded && (
        <div className="absolute inset-0 left-1/2 right-1/2 -z-10 -ml-[50vw] -mr-[50vw] w-screen overflow-hidden" aria-hidden>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${WALLPAPER}')` }}
          />
          <div className="absolute inset-0 bg-[#2b2c48]/25" />
        </div>
      )}

      <div className={embedded ? 'relative' : 'relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'}>
        <div className="work-with-card overflow-hidden rounded-[28px] bg-white shadow-[0_0_0_8px_rgba(255,255,255,0.2)]">
          {/* Credential bar */}
          <div className="flex flex-col gap-4 border-b border-[rgba(18,32,58,0.12)] px-6 py-5 sm:px-8 lg:grid lg:grid-cols-[96px_minmax(0,1fr)_auto] lg:items-start lg:gap-x-6 lg:gap-y-1.5 lg:px-10 lg:py-6">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4 lg:contents">
              <div
                role="img"
                aria-label="Tomas"
                className="tomas-headshot shrink-0 lg:row-span-2"
                style={{ ['--img-url' as string]: `url('${AVATAR}')` }}
              />
              <div className="min-w-0 lg:col-start-2 lg:row-start-1">
                <h2 className="text-[18px] font-bold tracking-[-0.02em] text-[#12203A] sm:text-[22px]">
                  Work with Tomas
                </h2>
                <p className="mt-0.5 text-[13px] font-medium text-[rgba(18,32,58,0.55)]">
                  Founder, San Diego, CA
                </p>
              </div>
            </div>

            <p className="max-w-[620px] text-[14.5px] leading-[1.55] text-[rgba(18,32,58,0.66)] lg:col-start-2 lg:row-start-2">
              20 years in marketing, CRO, CRM, and analytics. Now building the systems AI runs on.
              
            </p>

            <div className={`flex items-center gap-4 sm:gap-5 lg:col-start-3 lg:row-span-2 ${hideStats ? 'justify-center lg:justify-end' : 'flex-col items-center gap-3 sm:items-end'}`}>
            {!hideStats && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4 lg:flex lg:flex-wrap lg:justify-end lg:gap-x-8">
              {STATS.map((stat) => (
                <div key={stat.value} className="min-w-0 text-center lg:min-w-[88px] lg:text-left">
                  <div className="text-[18px] font-bold tracking-tight text-[#12203A] sm:text-[20px]">
                    {stat.value}
                  </div>
                  <div className="mt-0.5 text-[12px] leading-snug text-[rgba(18,32,58,0.5)]">
                    {stat.label}
                  </div>
                </div>
              ))}
              <div className="min-w-0 text-center lg:min-w-[88px] lg:text-left">
                <div className="flex items-center justify-center gap-1 text-[18px] font-bold tracking-tight text-[#12203A] sm:text-[20px] lg:justify-start">
                  <span>5.0</span>
                  <svg className="h-5 w-5 fill-amber-400 text-amber-400" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <a
                  href={REVIEWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-0.5 block text-[12px] leading-snug text-blue-600 underline hover:text-blue-800"
                >
                  Client Reviews
                </a>
              </div>
            </div>
            )}
            {hideStats ? (
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 lg:justify-end">
                <div className="min-w-0 text-center lg:text-left">
                  <div className="flex items-center justify-center gap-1 text-[18px] font-bold tracking-tight text-[#12203A] sm:text-[20px] lg:justify-start">
                    <span>5.0</span>
                    <svg className="h-5 w-5 fill-amber-400 text-amber-400" viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                  <a
                    href={REVIEWS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0.5 block text-[12px] leading-snug text-blue-600 underline hover:text-blue-800"
                  >
                    Client Reviews
                  </a>
                </div>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/images/badges/salesforce.png"
                  alt="Salesforce Certified Platform App Builder"
                  className="h-14 w-auto object-contain sm:h-16"
                />
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/images/badges/zoho.png"
                  alt="Zoho Creator Certified Developer Professional"
                  className="h-14 w-auto object-contain sm:h-16"
                />
              </div>
            ) : (
            <div className="flex items-center justify-center gap-2 lg:justify-end">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/images/badges/salesforce.png"
                alt="Salesforce Certified Platform App Builder"
                className="h-14 w-auto object-contain sm:h-16"
              />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/images/badges/zoho.png"
                alt="Zoho Creator Certified Developer Professional"
                className="h-14 w-auto object-contain sm:h-16"
              />
            </div>
            )}
            </div>
          </div>

          {/* Form beside proof */}
          <div className="grid gap-8 px-6 py-7 sm:px-8 sm:py-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-11 lg:px-10 lg:py-9">
            <div>
              <h3 className="text-[28px] font-bold leading-none tracking-[-0.03em] text-[#12203A] sm:text-[38px]">
                {heading}
              </h3>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <p className="text-[14.5px] leading-[1.5] text-[rgba(18,32,58,0.6)]">
                  You work with me directly, from scope to delivery.
                </p>
                <a
                  href={CALENDAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center gap-1.5 text-[14px] font-semibold text-[#13479c] transition-colors hover:text-[#0f3a7a]"
                  onClick={() => trackPostHogEvent('cta_click', {
                    cta_text: 'Schedule a 15min Call',
                    cta_location: formSource,
                    cta_type: 'calendar',
                  })}
                >
                  <Calendar className="h-3.5 w-3.5" aria-hidden />
                  Schedule a 15min Call
                </a>
              </div>

              <div className="mt-6 [&_form>div]:!space-y-3 [&_form>div_.grid]:!gap-3 [&_textarea]:min-h-[110px] [&_input]:rounded-[10px] [&_textarea]:rounded-[10px] [&_input]:border-[rgba(18,32,58,0.12)] [&_textarea]:border-[rgba(18,32,58,0.12)] [&_#website]:rounded-none [&_#website]:border-0 [&_form_.rounded-lg]:rounded-[10px] [&_form_.rounded-lg]:border-[rgba(18,32,58,0.12)] [&_button[type=submit]]:!bg-[#13479c] [&_button[type=submit]]:hover:!bg-[#0f3a7a] [&_button[type=submit]]:!shadow-[0_10px_24px_rgba(19,71,156,0.28)] [&_.absolute.-inset-px]:!hidden">
                <AuditForm
                  layout="column"
                  serviceType="monthly"
                  buttonText={buttonText}
                  ctaText="Tell us what you need"
                  formSource={formSource}
                  serviceName={serviceName}
                  focusAreaPlaceholder={focusAreaPlaceholder}
                />
              </div>

             
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-1 items-center justify-center overflow-hidden rounded-2xl bg-white">
                <img
                  src="/images/reviews.webp"
                  alt="Client testimonials"
                  className="h-auto w-full max-w-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        {showFooter && <Footer variant="onDark" />}
      </div>
    </section>
  )
}
